/**
 * Team management hooks (quick 260814-ud3 / ClickUp 86bbed5r5).
 *
 * All endpoints are MANAGE_USERS-gated and own-tenant-only server-side; the nav
 * gate is UX. Every mutation invalidates the shared ['drivers'] prefix as well as
 * the team list, because a status or role change can move someone in or out of
 * the dispatch-board roster and the Fleet list, which read different query keys.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';

export const TEAM_QUERY_KEY = ['users', 'team'] as const;

export interface TeamMember {
  readonly id: string;
  readonly fullName: string;
  readonly email: string;
  readonly phone: string | null;
  readonly role: string;
  readonly status: string;
  /** The caller's own row — self-deactivation and self-demotion are blocked. */
  readonly isSelf: boolean;
  /**
   * Phase 20.6e — false for a person who has lodeX rows but no login yet (an
   * import writes people with no Cognito account). They get Invite instead of
   * Reset password, which the server would refuse for them.
   *
   * Optional on purpose, and only `=== false` means "not invited": an API
   * that predates the field (the SPA can go live before the API task rolls)
   * omits it, and reading that absence as "not invited" would put an Invite
   * button on every person who already has a login.
   */
  readonly invited?: boolean;
  /**
   * Migration 0227 — SES flagged the address: BOUNCING after a hard bounce, COMPLAINED after a
   * spam complaint. The server sends this person nothing (no invite, no reset). Optional for an
   * API that predates it; absent means OK.
   */
  readonly emailStatus?: UserEmailStatus;
}

export type UserEmailStatus = 'OK' | 'BOUNCING' | 'COMPLAINED';

/** True when the server will not email this address (0227). */
export function isEmailFlagged(m: { readonly emailStatus?: string | undefined }): boolean {
  return m.emailStatus !== undefined && m.emailStatus !== 'OK';
}

/** True only when the API positively says this person has no login yet. */
export function isNotInvited(m: Pick<TeamMember, 'invited'>): boolean {
  return m.invited === false;
}

/** POST /v1/users/:id/invite response (DEC-283). */
export interface InviteResult {
  readonly id: string;
  readonly role: string;
  readonly email: string;
  readonly phone: string | null;
  readonly signInMethod: 'SMS_OTP' | 'EMAIL_PASSWORD';
  /** Office roles only: false = the login exists but the email did not send. */
  readonly inviteEmailed: boolean;
  /** 0227 — set when nothing happened because the address is flagged (no login was made). */
  readonly inviteEmailSkipped?: 'EMAIL_BOUNCING' | null;
}

/** POST /v1/users/:id/reset-password response. */
export interface ResetPasswordResult {
  readonly id: string;
  readonly emailed: boolean;
  /** 0227 — set when nothing was rotated because the address is flagged. */
  readonly emailSkipped?: 'EMAIL_BOUNCING' | null;
}

/**
 * Invite ONE person (Corbin's call — there is no bulk invite, on purpose).
 * Always sends `{}`: a body-less POST is a 400 at Fastify's JSON parser
 * (DEC-201).
 */
export function useInviteUser(
  userId: string,
): ReturnType<typeof useApiMutation<Record<string, never>, InviteResult>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateUserSurfaces();
  return useApiMutation<Record<string, never>, InviteResult>(
    opts,
    `/v1/users/${userId}/invite`,
    'POST',
    // Invalidate on error too: a 409 means the list was stale (someone else
    // invited them), and the refetch is what turns the button into "Invited".
    { onSettled: () => invalidate() },
  );
}

export function useTeam(
  includeDrivers = false,
): ReturnType<typeof useApiQuery<{ users: readonly TeamMember[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ users: readonly TeamMember[] }>(
    opts,
    includeDrivers ? '/v1/users?includeDrivers=1' : '/v1/users',
    { queryKey: [...TEAM_QUERY_KEY, includeDrivers], staleTime: 15_000 },
  );
}

/** Invalidate every surface that renders a user — team list, roster, Fleet. */
function useInvalidateUserSurfaces(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['users'] });
    void qc.invalidateQueries({ queryKey: ['drivers'] });
  };
}

export function useSetUserStatus(
  userId: string,
  onDone: () => void,
): ReturnType<typeof useApiMutation<{ active: boolean }, { id: string; status: string }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateUserSurfaces();
  return useApiMutation<{ active: boolean }, { id: string; status: string }>(
    opts,
    `/v1/users/${userId}/status`,
    'PATCH',
    {
      onSuccess: () => {
        invalidate();
        onDone();
      },
    },
  );
}

export function useSetUserRole(
  userId: string,
  onDone: () => void,
): ReturnType<typeof useApiMutation<{ role: string }, { id: string; role: string }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateUserSurfaces();
  return useApiMutation<{ role: string }, { id: string; role: string }>(
    opts,
    `/v1/users/${userId}/role`,
    'PATCH',
    {
      onSuccess: () => {
        invalidate();
        onDone();
      },
    },
  );
}

export function useResetUserPassword(
  userId: string,
  // Quick 260821-ses — receives the RESULT. This used to be `() => void`, which
  // discarded `emailed` and made the caller's success toast unconditional; the
  // API can now legitimately return `emailed: false` (password rotated, delivery
  // failed) and the operator has to be told the difference.
  onDone: (result: ResetPasswordResult) => void,
): ReturnType<typeof useApiMutation<Record<string, never>, ResetPasswordResult>> {
  const opts = useApiOpts();
  return useApiMutation<Record<string, never>, ResetPasswordResult>(
    opts,
    `/v1/users/${userId}/reset-password`,
    'POST',
    { onSuccess: (result) => onDone(result) },
  );
}
